// Copyright (c) 2022-2024. Sendanor <info@sendanor.fi>. All rights reserved.

import {
    ReactNode,
    useState,
} from "react";
import { ButtonStyle } from "../../../../../../../io/hyperify/core/frontend/button/ButtonStyle";
import { find } from "../../../../../../../io/hyperify/core/functions/find";
import { map } from "../../../../../../../io/hyperify/core/functions/map";
import { reduce } from "../../../../../../../io/hyperify/core/functions/reduce";
import { LogService } from "../../../../../../../io/hyperify/core/LogService";
import {
    getProductTypeTranslationKey,
} from "../../../../../../../io/hyperify/core/store/constants/storeTranslation";
import { DomainSearchResult } from "../../../../../../../io/hyperify/core/store/types/domain/DomainSearchResult";
import { DomainSearchState } from "../../../../../../../io/hyperify/core/store/types/domain/DomainSearchState";
import { Product } from "../../../../../../../io/hyperify/core/store/types/product/Product";
import { ProductPrice } from "../../../../../../../io/hyperify/core/store/types/product/ProductPrice";
import { ProductPriceType } from "../../../../../../../io/hyperify/core/store/types/product/ProductPriceType";
import { ProductType } from "../../../../../../../io/hyperify/core/store/types/product/ProductType";
import { TranslationFunction } from "../../../../../../../io/hyperify/core/types/TranslationFunction";
import { ComponentUtils } from "../../../../../../../io/hyperify/frontend/components/utils/ComponentUtils";
import { useStoreProductList } from "../../../../../../../io/hyperify/frontend/hooks/useStoreProductList";
import {
    DOMAIN_SALE_LIST_VIEW_CLASS_NAME,
} from "../../../../../core/constants/className";
import {
    DEFAULT_SELECTED_PRICE_TYPE_FOR_DOMAINS,
} from "../../../../../core/constants/frontend";
import {
    DOMAIN_SALE_ROUTE,
    DOMAINS_SALE_LIST_ROUTE,
} from "../../../../../core/constants/route";
import {
    T_COMMON_PRODUCT_PRICE_INFO,
    T_DATE_TIME_FORMAT,
    T_DOMAIN_DESCRIPTION,
    T_DOMAIN_TITLE,
    T_ORDER_NOW,
    T_PREMIUM_DOMAIN_INTRO,
    T_PREMIUM_DOMAIN_TITLE,
    T_READ_MORE,
    T_SITE_NAME,
} from "../../../../../core/constants/translation";
import {
    DomainsIcon,
} from "../../../../assets/icons";
import { useDomainSearch } from "../../../../hooks/useDomainSearch";
import { useProductModelList } from "../../../../hooks/useProductModelList";
import { CardList } from "../../../common/card/CardList";
import { SEODiv } from "../../../common/seo/SEODiv";
import { ArticleHeader } from "../../../common/title/ArticleHeader";
import "./DomainSaleListView.scss";

const LOG = LogService.createLogger( 'DomainSaleListView' );

export interface DomainSaleListViewProps {
    readonly t : TranslationFunction;
    readonly className? : string;
}

export function DomainSaleListView ( props : DomainSaleListViewProps ) {
    const t = props.t;
    const className = props?.className;
    const [ searchString ] = useState<string>( '' );

    LOG.debug( `searchString = `, searchString );

    const [priceType] = useState<ProductPriceType>(DEFAULT_SELECTED_PRICE_TYPE_FOR_DOMAINS);
    LOG.debug( `priceType = `, priceType );

    const domainProductList = useStoreProductList( ProductType.DOMAIN );
    LOG.debug( `domainProductList = `, domainProductList );

    const [ dto ] = useDomainSearch( searchString );

    const resultList : readonly DomainSearchResult[] | undefined = dto?.results;
    const hasResults : boolean = dto !== undefined;
    const isSearching : boolean = dto?.state === DomainSearchState.SEARCHING;

    LOG.debug( `isSearching = `, isSearching );
    LOG.debug( `hasResults = `, hasResults );
    LOG.debug( `resultList = `, resultList );

    const domainProductIdList = map(
        domainProductList,
        (item) : string => item.id
    );

    const customProductList : readonly Product[] = reduce(
        resultList,
        ( list : readonly Product[], item : DomainSearchResult ) : readonly Product[] => {
            const itemProductList : readonly Product[] = item?.productList;
            if ( itemProductList ) {
                return [
                    ...list,
                    ...map(
                        itemProductList,
                        (item: Product) : Product => {
                            if (item.id) {
                                const foundItem : Product | undefined = find(
                                    domainProductList,
                                    (i: Product) : boolean => i.id === item.id
                                );
                                return foundItem ? foundItem : item;
                            }
                            return item;
                        }
                    )
                ];
            }
            return list;
        },
        [],
    );
    LOG.debug( `customProductList = `, customProductList );

    const paramBlocks = {};

    const productModelList = useProductModelList(
        customProductList,
        priceType,
        t( T_DATE_TIME_FORMAT ),
        t,
        () => DomainsIcon,
        ( item: Product ) : string | undefined =>
            domainProductIdList.includes(item.id) ? DOMAIN_SALE_ROUTE( item.id ) : undefined,
        (item: Product) => domainProductIdList.includes( item.id ) ? T_READ_MORE : T_ORDER_NOW,
        (item: Product, price: ProductPrice | undefined) : ProductPrice | undefined =>
            domainProductIdList.includes( item.id ) ? undefined : price,
        (item) => {
            return (
                domainProductIdList.includes( item.id )
                    ? (item.type === ProductType.DOMAIN ? ButtonStyle.PRIMARY : ButtonStyle.SECONDARY)
                    : undefined
            );
        },
        (item: Product, price, translationParams) : string => {
            return `${t(getProductTypeTranslationKey( item.type ), translationParams)} ${item.title}`;
        },
        (item: Product, _price, translationParams) : ReactNode => {
            return ComponentUtils.prepareParagraphNodes(
                t,
                [
                    ...(t(item.summary, translationParams).split(/\n+/)),
                    ...(t(T_COMMON_PRODUCT_PRICE_INFO, translationParams).split(/\n+/)),
                ],
                paramBlocks,
                translationParams
            );
        },
    );
    LOG.debug( `productModelList = `, productModelList );

    const domainProductModelList = useProductModelList(
        domainProductList,
        priceType,
        t( T_DATE_TIME_FORMAT ),
        t,
        () => DomainsIcon,
        ( item : Product ) : string => DOMAIN_SALE_ROUTE(item.id),
        () => undefined,
        () => undefined,
        () => undefined,
        (item: Product) => item.title,
        (item: Product, _price, translationParams) : ReactNode => {
            return ComponentUtils.prepareParagraphNodes(
                t,
                [
                    ...(t(item.summary, translationParams).split(/\n+/)),
                    ...(t(T_COMMON_PRODUCT_PRICE_INFO, translationParams).split(/\n+/)),
                ],
                paramBlocks,
                translationParams
            );
        },
    );
    LOG.debug( `domainProductModelList = `, domainProductModelList );

    return (
        <SEODiv
            className={
                DOMAIN_SALE_LIST_VIEW_CLASS_NAME
                + (className ? ` ${ className }` : "")
            }
            path={DOMAINS_SALE_LIST_ROUTE}
            title={ t( T_DOMAIN_TITLE ) }
            description={ t( T_DOMAIN_DESCRIPTION ) }
            siteName={ t( T_SITE_NAME ) }
        >

            <article className={ DOMAIN_SALE_LIST_VIEW_CLASS_NAME + '-article' }>

                <ArticleHeader
                    title={ t( T_PREMIUM_DOMAIN_TITLE ) }
                    description={ t( T_PREMIUM_DOMAIN_INTRO ) }
                    className={ `${ DOMAIN_SALE_LIST_VIEW_CLASS_NAME + '-article-header' }` }
                    t={ t }
                />

                <CardList
                    productList={ domainProductModelList }
                    buttonLabel={ t(T_READ_MORE) }
                    className={ `${ DOMAIN_SALE_LIST_VIEW_CLASS_NAME }-article-content-results-product` }
                    t={ t }
                    dark={ false }
                >
                </CardList>

            </article>

        </SEODiv>
    );
}
